import React from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { ATMOSPHERE } from '../data/products';
import Breadcrumbs from '../components/Breadcrumbs';
import ChapterBreak from '../components/ChapterBreak';
import { Reveal } from '../components/Reveal';

const PILLARS = [
  { id:'precision', n:'01', title:'Precision', lead:'Automotive tolerances across every joint.',
    specs:[['Tolerance','±2 mm'],['Method','CNC + jig-driven welds'],['QA','Every unit tested']] },
  { id:'structural', n:'02', title:'Structural Integrity', lead:'Corrosion-protected steel frame engineered for the long term.',
    specs:[['Frame','Steel · powder-coated'],['Loads','Wind · seismic ready'],['Life','50+ year design']] },
  { id:'materials', n:'03', title:'Materials', lead:'Every layer is selected for durability and finish.',
    specs:[['Shell','Aluminium composite'],['Paint','2K automotive · Class A'],['Floor','SPC · waterproof']] },
  { id:'insulation', n:'04', title:'Insulation', lead:'A continuous thermal and acoustic envelope.',
    specs:[['Core','PU · high density'],['Glazing','Double · low-E'],['Seal','Vapour-tight']] },
  { id:'transport', n:'05', title:'Transportability', lead:'A finished product delivered as a single module.',
    specs:[['Delivery','Single-piece'],['Mode','Road · sea'],['Reach','Worldwide']] },
  { id:'install', n:'06', title:'Installation', lead:'Placed, levelled and connected. Never constructed.',
    specs:[['Timeline','1–3 days'],['Site work','Foundation + utilities'],['Handover','Turnkey']] },
  { id:'warranty', n:'07', title:'Warranty', lead:'Product-grade warranty, not project-grade.',
    specs:[['Structural','10 yr'],['Envelope','5 yr'],['MEP','2 yr']] },
];

const ROADMAP = [
  ['2026','IS-code alignment · in-house QA'],
  ['2027','ISO 9001 · CE marking pathway'],
  ['2028','Third-party certifications · global markets'],
];

export default function Engineering() {
  return (
    <main data-testid="engineering-page" className="bg-white text-[#111]">
      <Helmet>
        <title>Engineering | MODULAROOM</title>
        <meta name="description" content="Every MODULAROOM is engineered to product-grade tolerances using off-site construction and factory-controlled manufacturing. Structural steel, insulated envelope, 10-year warranty." />
        <link rel="canonical" href="https://modularoom.com/engineering" />
      </Helmet>
      <section className="pt-40 md:pt-56 pb-16 max-w-[1440px] mx-auto px-6 md:px-12">
        <Breadcrumbs items={[{label:'Home', to:'/'}, {label:'Design & Engineering'}, {label:'Engineering'}]} />
        <h1 className="mt-14 font-display text-6xl md:text-7xl lg:text-8xl tracking-tight leading-[0.95] max-w-5xl">
          Engineered<br/><span className="italic text-black/40">to product tolerances.</span>
        </h1>
        <p className="mt-10 max-w-xl text-[17px] text-black/60 leading-[1.75]">
          MODULAROOM is manufactured in a controlled environment to standards more typical of automotive than architecture. Site work is limited to placement and utilities.
        </p>
      </section>

      <ChapterBreak src={ATMOSPHERE.technology} chapter="Engineering" minHeight="60svh" />

      <section className="relative bg-[#111] text-white py-32 md:py-48 overflow-hidden">
        <div className="absolute inset-0 blueprint-grid-dark pointer-events-none" />
        <div className="relative max-w-[1440px] mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-px bg-white/10 border border-white/10">
            {PILLARS.map((c) => (
              <div key={c.id} id={c.id} className="bg-[#111] p-8 md:p-10 min-h-[280px] flex flex-col">
                <div className="flex items-center justify-between">
                  <div className="text-[10px] tracking-[0.32em] uppercase text-[#C7A36A]">{c.n}</div>
                  <div className="font-mono-luxe text-[11px] text-white/40">— {c.title.toUpperCase()}</div>
                </div>
                <div className="mt-6 font-display text-2xl md:text-3xl tracking-tight">{c.title}</div>
                <div className="mt-3 text-sm text-white/60 leading-relaxed max-w-xs">{c.lead}</div>
                <div className="mt-auto pt-8 border-t border-white/10 grid grid-cols-1 gap-1.5 font-mono-luxe text-[12px] text-white/85">
                  {c.specs.map(([k, v]) => (
                    <div key={k} className="flex justify-between">
                      <span className="text-white/40">{k}</span><span>{v}</span>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>

          <div id="warranty" className="mt-24 border-t border-white/10 pt-12 grid grid-cols-1 md:grid-cols-4 gap-8">
            <div className="text-[10px] tracking-[0.32em] uppercase text-white/50">Future Roadmap</div>
            <div className="md:col-span-3 grid grid-cols-1 sm:grid-cols-3 gap-8">
              {ROADMAP.map(([y, n]) => (
                <div key={y}>
                  <div className="font-mono-luxe text-[#C7A36A] text-lg">{y}</div>
                  <div className="mt-2 text-sm text-white/70 leading-relaxed">{n}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Related */}
      <section className="bg-white py-24 md:py-32">
        <div className="max-w-[1440px] mx-auto px-6 md:px-12">
          <div className="text-[10px] tracking-[0.32em] uppercase text-black/40 mb-6">Continue reading</div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              ['Technology','/technology','The systems inside every MODULAROOM.'],
              ['Materials & Finishes','/materials','The palette that makes it feel considered.'],
              ['Book a Consultation','/contact#book','Discuss your site and specifications.'],
            ].map(([t, to, d]) => (
              <Link key={t} to={to} className="group block border-t border-black/10 pt-6">
                <div className="font-display text-2xl tracking-tight group-hover:text-[#C7A36A] transition-colors">{t}</div>
                <div className="mt-3 text-sm text-black/60">{d}</div>
              </Link>
            ))}
          </div>
        </div>
      </section>
    </main>
  );
}
